import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTheme } from "./ThemeContext";

export default function ContactUs() {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, you would send this to a backend or email service
    console.log('Form submission:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --ink: #0f1117;
      --ink-soft: #4a4f5e;
      --bg: #f7f8fa;
      --surface: #ffffff;
      --border: #e4e6ed;
      --grad: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    }
    
    [data-theme="dark"] {
      --ink: #f7f8fa;
      --ink-soft: #cbd5e1;
      --bg: #0f1117;
      --surface: #1a1f3a;
      --border: #2d3748;
    }
    
    body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); line-height: 1.8; }
    
    nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(247,248,250,.88);
      backdrop-filter: blur(14px);
      border-bottom: 1px solid var(--border);
      padding: 0 2rem;
      display: flex; align-items: center; justify-content: space-between;
      height: 64px;
    }
    [data-theme="dark"] nav { background: rgba(15,17,23,.88); }
    
    .nav-back { cursor: pointer; font-size: 1.2rem; color: var(--ink); transition: opacity .2s; }
    .nav-back:hover { opacity: .6; }
    
    .nav-theme-toggle {
      background: transparent; border: none; cursor: pointer;
      font-size: 1.2rem; color: var(--ink);
      padding: .4rem; border-radius: 6px;
      transition: background .2s;
    }
    .nav-theme-toggle:hover { background: rgba(102,126,234,.1); }
    
    .hero {
      padding: 5rem 2rem;
      background: linear-gradient(180deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.04) 100%);
      text-align: center;
      max-width: 1000px;
      margin: 0 auto;
    }
    
    h1 {
      font-family: 'DM Serif Display', serif;
      font-size: 2.8rem;
      margin-bottom: 1rem;
      background: var(--grad);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .subtitle {
      font-size: 1.3rem;
      color: var(--ink-soft);
      max-width: 700px;
      margin: 0 auto;
    }
    
    .content {
      max-width: 800px;
      margin: 0 auto;
      padding: 3rem 2rem;
    }
    
    .intro-text {
      text-align: center;
      color: var(--ink-soft);
      margin-bottom: 2.5rem;
      font-size: 1.1rem;
    }
    
    .form-group {
      margin-bottom: 1.8rem;
    }
    
    label {
      display: block;
      margin-bottom: .6rem;
      font-weight: 600;
      color: var(--ink);
      font-size: .95rem;
    }
    
    input, textarea {
      width: 100%;
      padding: .9rem 1rem;
      border: 1px solid var(--border);
      border-radius: 10px;
      font-family: 'DM Sans', sans-serif;
      font-size: 1rem;
      color: var(--ink);
      background: var(--surface);
      transition: all .2s;
    }
    
    input:focus, textarea:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102,126,234,.1);
    }
    
    textarea {
      resize: vertical;
      min-height: 150px;
    }
    
    .submit-btn {
      background: var(--grad);
      color: #fff;
      padding: 1rem 2rem;
      border: none;
      border-radius: 10px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all .2s;
      width: 100%;
      margin-top: .5rem;
    }
    
    .submit-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102,126,234,.4);
    }
    
    .submit-btn:active {
      transform: translateY(0);
    }
    
    .success-message {
      background: linear-gradient(135deg, rgba(34,197,94,.1) 0%, rgba(59,130,246,.08) 100%);
      border: 1px solid rgba(34,197,94,.3);
      border-radius: 10px;
      padding: 1.5rem;
      margin-bottom: 2rem;
      color: #16a34a;
      text-align: center;
      font-weight: 500;
      animation: slideDown .3s ease;
    }
    
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .contact-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      margin-top: 3rem;
      padding-top: 3rem;
      border-top: 1px solid var(--border);
    }
    
    .info-item {
      text-align: center;
    }
    
    .info-icon {
      font-size: 2rem;
      margin-bottom: .8rem;
    }
    
    .info-item h3 {
      font-size: 1.1rem;
      margin-bottom: .5rem;
      color: var(--ink);
    }
    
    .info-item p {
      color: var(--ink-soft);
      margin: 0;
      font-size: .95rem;
    }
    
    .info-item a {
      color: #667eea;
      text-decoration: none;
      transition: color .2s;
    }
    
    .info-item a:hover {
      color: #764ba2;
      text-decoration: underline;
    }
    
    footer {
      border-top: 1px solid var(--border);
      padding: 2rem;
      text-align: center;
      color: var(--ink-soft);
      margin-top: 4rem;
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav>
        <div className="nav-back" onClick={() => navigate('/')}>← Back</div>
        <button className="nav-theme-toggle" onClick={toggleTheme}>
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      </nav>

      <div className="hero">
        <h1>Get in Touch</h1>
        <p className="subtitle">
          Have questions about our tools or tutorials? We'd love to hear from you!
        </p>
      </div>

      <div className="content">
        <p className="intro-text">
          Fill out the form below and we'll get back to you as soon as possible. 
          Whether you have feedback, questions, or partnership inquiries, we're here to help.
        </p>

        {submitted && (
          <div className="success-message">
            ✓ Thank you! Your message has been sent successfully.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="How can we help?"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us more about your inquiry..."
              required
            ></textarea>
          </div>

          <button type="submit" className="submit-btn">
            Send Message
          </button>
        </form>

        <div className="contact-info">
          <div className="info-item">
            <div className="info-icon">📧</div>
            <h3>Email</h3>
            <p><a href="mailto:hello@flycodeinv.com">hello@flycodeinv.com</a></p>
          </div>
          <div className="info-item">
            <div className="info-icon">🌐</div>
            <h3>Website</h3>
            <p><a href="https://flycodeinv.com" target="_blank" rel="noopener noreferrer">flycodeinv.com</a></p>
          </div>
          <div className="info-item">
            <div className="info-icon">⏱️</div>
            <h3>Response Time</h3>
            <p>Usually within 24-48 hours</p>
          </div>
        </div>
      </div>

      <footer>
        <p>© 2026 Fly Code Innovation | All Rights Reserved</p>
      </footer>
    </>
  );
}
